/**
 * The app's OWN sound cues: join/leave, the message and mention pings, the
 * screen-share chime.
 *
 * EVERY cue in the app goes through this module, and every cue is gated on the
 * «Системные звуки» switch (Настройки → Уведомления). That gate is the whole
 * point of routing them through here: it must silence what Bunker says ABOUT
 * itself without touching CONTENT — people's voices, shared screen sound and
 * voice messages play through the voice pipeline and VoiceNotePlayer, never
 * through this file, so they are unaffected by design rather than by a list of
 * exceptions someone has to maintain.
 *
 * Most cues are synthesised with WebAudio from the recipes below (the owner
 * picked them by ear on a sound board, 26.09.2026): join is a warm synth chord
 * whose filter opens, a message is a short square blip, a mention or DM is a
 * double water drop, a screen share is a quick digital arpeggio with an echo.
 * Each event has its own timbre, so they can be told apart without looking.
 * Leave keeps the user-supplied mp3 in `client/public/sounds/`.
 *
 * All synthesised cues go through one bus (gain → gentle compressor) so they
 * sit at the loudness the owner heard when choosing, below the conversation —
 * they announce, they don't compete.
 */

import { areSystemSoundsMuted } from '@/lib/uiPrefs';

// ── leave clips (mp3) ───────────────────────────────────────────────────────
// One HTMLAudioElement per clip, rewound before each play: no GC churn and no
// "too many simultaneous decoders" on rapid leaves.

const CLIPS = {
  selfLeave: { src: '/sounds/voice-leave.mp3', volume: 0.35 },
  peerLeave: { src: '/sounds/voice-leave.mp3', volume: 0.28 },
} as const;

type ClipKey = keyof typeof CLIPS;
const clipCache = new Map<ClipKey, HTMLAudioElement>();

function playClip(key: ClipKey): void {
  if (areSystemSoundsMuted()) return;
  try {
    let el = clipCache.get(key);
    if (!el) {
      el = new Audio(CLIPS[key].src);
      el.preload = 'auto';
      el.volume = CLIPS[key].volume;
      clipCache.set(key, el);
    }
    // Rewind so a second leave in a row restarts the clip instead of being ignored.
    el.currentTime = 0;
    const result = el.play();
    // play() rejects before the first user gesture (autoplay policy); sounds
    // work once the user clicks anywhere, so there is nothing to report.
    if (result && typeof result.then === 'function') result.catch(() => {});
  } catch {
    /* play() can throw synchronously on detached / not-ready states */
  }
}

// ── synthesised cues ────────────────────────────────────────────────────────

/** One note of a cue. Pitches are MIDI note numbers. */
export interface CueNote {
  note: number;
  /** Start of a pitch glide into `note` (MIDI), over `glide` seconds. */
  fromNote?: number;
  glide?: number;
  wave: OscillatorType;
  /** Detuned copies in cents; two slightly apart make a synth chord warm. */
  detune?: readonly number[];
  /** Start offset within the cue, seconds. */
  at?: number;
  attack: number;
  /** Total length: attack + exponential tail, seconds. */
  dur: number;
  gain: number;
  /** Low-pass cut-off in Hz; with `lpFrom` the filter sweeps to it over `lpTime`. */
  lp: number;
  lpFrom?: number;
  lpTime?: number;
  q?: number;
  /** A damped echo: delay time, feedback and wet level. */
  echo?: { time: number; feedback: number; wet: number };
}

const chord = (notes: number[], note: Omit<CueNote, 'note'>): CueNote[] =>
  notes.map((n) => ({ ...note, note: n }));
const arpeggio = (notes: number[], step: number, note: Omit<CueNote, 'note' | 'at'>): CueNote[] =>
  notes.map((n, i) => ({ ...note, note: n, at: i * step }));

const WARM_SAW = { wave: 'sawtooth', detune: [-9, 9], attack: 0.02, q: 2 } as const;

type CueKey = 'selfJoin' | 'peerJoin' | 'message' | 'mention' | 'screenShare';

export const CUE_RECIPES: Record<CueKey, readonly CueNote[]> = {
  // «Синтезатор»: A-major chord, the filter opens — "you're in".
  selfJoin: chord([57, 64, 69, 73], { ...WARM_SAW, lpFrom: 350, lp: 3000, lpTime: 0.28, dur: 0.5, gain: 0.09 }),
  // Same gesture, fewer voices, quieter and shorter: someone else arrived.
  peerJoin: chord([64, 69, 73], { ...WARM_SAW, lpFrom: 350, lp: 1800, lpTime: 0.2, dur: 0.3, gain: 0.06 }),
  // «Синтезатор»: one short square blip — the most frequent cue, so the lightest.
  message: [{ note: 88, wave: 'square', attack: 0.002, dur: 0.07, gain: 0.1, lp: 3500, q: 0.5 }],
  // «Тактильный»: two rising water drops — clearly "this one is for you".
  mention: [
    { note: 86, fromNote: 74, glide: 0.05, wave: 'sine', attack: 0.002, dur: 0.12, gain: 0.3, lp: 6000 },
    { note: 93, fromNote: 81, glide: 0.05, wave: 'sine', at: 0.09, attack: 0.002, dur: 0.12, gain: 0.28, lp: 7000 },
  ],
  // «Сонар»: a quick digital arpeggio with an echo — "something started".
  screenShare: arpeggio([72, 76, 79, 84, 88], 0.035, {
    wave: 'square',
    attack: 0.005,
    dur: 0.07,
    gain: 0.07,
    lp: 3200,
    echo: { time: 0.13, feedback: 0.35, wet: 0.35 },
  }),
};

const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

/** Bus level the owner chose the sounds at on the sound board. */
const BUS_GAIN = 0.44;

let cueCtx: AudioContext | null = null;
let cueBus: AudioNode | null = null;

function getCueOutput(): { ctx: AudioContext; bus: AudioNode } | null {
  try {
    if (!cueCtx || cueCtx.state === 'closed') {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      const ctx = new Ctor();
      const level = ctx.createGain();
      level.gain.value = BUS_GAIN;
      // Evens out chords and single blips so no cue jumps out.
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -24;
      comp.ratio.value = 3;
      comp.attack.value = 0.003;
      comp.release.value = 0.12;
      level.connect(comp).connect(ctx.destination);
      cueCtx = ctx;
      cueBus = level;
    }
    // Autoplay policy: resume is best-effort. Every cue follows some click
    // (joining a channel, opening the app), so this practically always succeeds.
    if (cueCtx.state === 'suspended') void cueCtx.resume().catch(() => {});
    return cueBus ? { ctx: cueCtx, bus: cueBus } : null;
  } catch {
    return null;
  }
}

function playNote(ctx: AudioContext, bus: AudioNode, n: CueNote, t0: number): void {
  const t = t0 + (n.at ?? 0);
  const end = t + n.dur + 0.05;

  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(n.gain, t + n.attack);
  env.gain.exponentialRampToValueAtTime(0.0001, t + n.dur);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = n.q ?? 0.5;
  if (n.lpFrom) {
    filter.frequency.setValueAtTime(n.lpFrom, t);
    filter.frequency.exponentialRampToValueAtTime(n.lp, t + (n.lpTime ?? n.dur));
  } else {
    filter.frequency.setValueAtTime(n.lp, t);
  }
  env.connect(filter).connect(bus);

  if (n.echo) {
    const delay = ctx.createDelay(1);
    delay.delayTime.value = n.echo.time;
    const feedback = ctx.createGain();
    feedback.gain.value = n.echo.feedback;
    const damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2600;
    const wet = ctx.createGain();
    wet.gain.value = n.echo.wet;
    filter.connect(delay);
    delay.connect(damp);
    damp.connect(feedback);
    feedback.connect(delay);
    damp.connect(wet);
    wet.connect(bus);
  }

  const detunes = n.detune ?? [0];
  for (const cents of detunes) {
    const osc = ctx.createOscillator();
    osc.type = n.wave;
    osc.detune.value = cents;
    if (n.fromNote !== undefined) {
      osc.frequency.setValueAtTime(hz(n.fromNote), t);
      osc.frequency.exponentialRampToValueAtTime(hz(n.note), t + (n.glide ?? 0.06));
    } else {
      osc.frequency.setValueAtTime(hz(n.note), t);
    }
    const share = ctx.createGain();
    share.gain.value = 1 / detunes.length;
    osc.connect(share).connect(env);
    osc.start(t);
    osc.stop(end);
  }
}

function playCue(key: CueKey): void {
  if (areSystemSoundsMuted()) return;
  const out = getCueOutput();
  if (!out) return;
  try {
    const t0 = out.ctx.currentTime + 0.02;
    for (const n of CUE_RECIPES[key]) playNote(out.ctx, out.bus, n, t0);
  } catch {
    /* WebAudio unavailable / context died — the cue is non-essential */
  }
}

/** I just joined a voice channel. */
export function playSelfJoinSound(): void {
  playCue('selfJoin');
}

/** Someone else joined the channel I'm currently in. */
export function playPeerJoinSound(): void {
  playCue('peerJoin');
}

/** Someone else left the channel I'm currently in. */
export function playPeerLeaveSound(): void {
  playClip('peerLeave');
}

/** I just left a voice channel (clicked Отключиться, kicked, force-moved). */
export function playSelfLeaveSound(): void {
  playClip('selfLeave');
}

/** Incoming text message I should hear about. Caller must already
 * have applied mute/visibility gating. */
export function playMessageSound(): void {
  playCue('message');
}

/** A message addressed to me: a mention, a reply to me or a direct message.
 * Caller must already have applied mute/visibility gating. */
export function playMentionSound(): void {
  playCue('mention');
}

/** A peer in my current voice channel just started sharing their screen. */
export function playScreenShareSound(): void {
  playCue('screenShare');
}
